import axios from 'axios';
import { getToken, clearSession } from '../Utils/session';

/**
 * Shared axios instance for the Administration API.
 * - Base URL from REACT_APP_API_BASE_URL (admin-dev / admin per environment).
 * - Request: attach "Authorization: Bearer <accessToken>" when a token exists.
 * - Response (handoff section 19):
 *     401 -> clear the session and bounce to /login.
 *     403 -> keep the session; let the screen render Access Denied.
 *   Other errors are passed through so screens can show the API's message.
 */

const axiosClient = axios.create({
  // Empty in dev (relative paths -> CRA proxy -> dev API, no CORS); absolute in prod.
  baseURL: process.env.REACT_APP_API_BASE_URL || '',
  headers: { 'Content-Type': 'application/json' },
});

axiosClient.interceptors.request.use((config) => {
  const token = getToken();
  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

axiosClient.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error?.response?.status;
    if (status === 401) {
      clearSession();
      // Avoid redirect loops when the failing call is the login request itself.
      if (!window.location.pathname.startsWith('/login')) {
        const from = window.location.pathname + window.location.search;
        window.location.assign(`/login?returnTo=${encodeURIComponent(from)}`);
      }
    }
    // 403 and everything else flow through to the caller unchanged.
    return Promise.reject(error);
  }
);

/** Normalize an axios error into a user-facing message + status for screens. */
export const toApiError = (error) => {
  const status = error?.response?.status;
  const data = error?.response?.data;
  const message =
    data?.message ||
    data?.title ||
    (typeof data === 'string' ? data : null) ||
    error?.message ||
    'Something went wrong. Please try again.';
  return { status, message, data };
};

export default axiosClient;
